import { useState, useEffect, useCallback, useRef } from "react";
import {
  getRakennusluvatByKiinteisto,
  createRakennuslupaRivi,
  updateRakennuslupaRivi,
  deleteRakennuslupaRivi,
} from "../../api/api";

// Live-DB sync for RakennuslupaTaulukko.jsx's Taloyhtiokortti-tab usage —
// same patterns as useMaaraaikaistarkastusData.js, just flat (no groups,
// no versioning: Rakennuslupataulukko stays unversioned per the plan).

const ROW_FIELDS = ["lupatunnus", "tyyppi", "lahde", "myonnetty", "huomiot"];

function pickRowFields(row) {
  const out = {};
  for (const f of ROW_FIELDS) if (row[f] !== undefined) out[f] = row[f];
  return out;
}

const apiErrorMessage = (e) => e?.response?.data?.error || e.message;

// Same local-id -> real-id resolver as useMaaraaikaistarkastusData.js — a
// row created this session keeps its crypto.randomUUID() as its React key
// for the mount's lifetime; this maps it to the real DB id once the
// create POST resolves, and lets an update/delete that arrives first
// simply await the same promise.
function makeIdResolver() {
  const idMap = new Map();
  const pending = new Map();
  return {
    async resolve(id) {
      if (idMap.has(id)) return idMap.get(id);
      if (pending.has(id)) return pending.get(id);
      return id;
    },
    track(localId, promise) {
      const tracked = promise.then(realId => { idMap.set(localId, realId); return realId; });
      pending.set(localId, tracked);
      tracked.finally(() => pending.delete(localId));
      return tracked;
    },
  };
}

export default function useRakennuslupaData(kiinteistoId) {
  const [data, setData] = useState(null); // { rows: [...] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pendingSavesRef = useRef(0);
  const beginSave = useCallback(() => { pendingSavesRef.current += 1; setSaving(true); }, []);
  const endSave = useCallback(() => {
    pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    if (pendingSavesRef.current === 0) setSaving(false);
  }, []);

  // Brief "Tallennettu" confirmation after a successful write — same idea
  // as useMaaraaikaistarkastusData.js's own flashSaved().
  const [savedFlash, setSavedFlash] = useState(false);
  const savedFlashTimerRef = useRef(null);
  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    clearTimeout(savedFlashTimerRef.current);
    savedFlashTimerRef.current = setTimeout(() => setSavedFlash(false), 1800);
  }, []);

  const rowIds = useRef(makeIdResolver());
  const loadSeqRef = useRef(0);

  useEffect(() => {
    if (!kiinteistoId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    rowIds.current = makeIdResolver();
    const seq = ++loadSeqRef.current;
    getRakennusluvatByKiinteisto(kiinteistoId)
      .then(res => {
        if (cancelled || seq !== loadSeqRef.current) return;
        setData({ rows: res.data });
      })
      .catch(e => { if (!cancelled) setError(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kiinteistoId]);

  const onRowAdd = useCallback(async (row) => {
    if (!kiinteistoId) return;
    beginSave();
    try {
      const promise = createRakennuslupaRivi({ id_kiinteisto: kiinteistoId, ...pickRowFields(row) })
        .then(res => res.data.id);
      rowIds.current.track(row.id, promise);
      await promise;
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [kiinteistoId, beginSave, endSave, flashSaved]);

  const onRowFieldChange = useCallback(async (rowId, field, value) => {
    beginSave();
    try {
      const realId = await rowIds.current.resolve(rowId);
      await updateRakennuslupaRivi(realId, { [field]: value });
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  const onRowDelete = useCallback(async (rowId) => {
    beginSave();
    try {
      const realId = await rowIds.current.resolve(rowId);
      await deleteRakennuslupaRivi(realId);
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  return { data, loading, saving, error, savedFlash, onRowAdd, onRowFieldChange, onRowDelete };
}
